import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar as CalendarIcon, Clock, User, Phone, Mail, ChevronRight, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = {
  APPOINTMENT_TYPE: 1,
  PRACTITIONER: 2,
  DATE_TIME: 3,
  PATIENT_INFO: 4,
  CONFIRMATION: 5,
  SUCCESS: 6
};

export default function BookingPage() {
  const { clinicSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check if embed mode is active
  const isEmbedMode = searchParams.get('embed') === 'true';
  
  // Clinic data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clinicInfo, setClinicInfo] = useState(null);
  
  // Booking state
  const [currentStep, setCurrentStep] = useState(STEPS.APPOINTMENT_TYPE);
  const [selectedAppointmentType, setSelectedAppointmentType] = useState(null);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Patient info
  const [patientInfo, setPatientInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    reason: '',
    honeypot: '' // Bot trap - must stay empty
  });
  
  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);

  // Fetch clinic info
  useEffect(() => {
    const fetchClinicInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/booking/clinic/${clinicSlug}`);
        if (!res.ok) {
          if (res.status === 404) {
            setError('This clinic does not have online booking enabled.');
          } else {
            setError('Failed to load booking page.');
          }
          setLoading(false);
          return;
        }
        const data = await res.json();
        setClinicInfo(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to connect to booking service.');
        setLoading(false);
      }
    };
    fetchClinicInfo();
  }, [clinicSlug]);

  // Fetch available slots when date changes
  const fetchSlots = useCallback(async (date) => {
    if (!date || !selectedAppointmentType) return;
    
    setLoadingSlots(true);
    try {
      const res = await fetch(`${API_URL}/api/public/booking/clinic/${clinicSlug}/slots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointment_type_id: selectedAppointmentType.id,
          practitioner_id: selectedPractitioner?.id || null,
          date: date
        })
      });
      if (res.ok) {
        const data = await res.json();
        setAvailableSlots(data.slots || []);
      } else {
        setAvailableSlots([]);
      }
    } catch (err) {
      setAvailableSlots([]);
    }
    setLoadingSlots(false);
  }, [clinicSlug, selectedAppointmentType, selectedPractitioner]);

  useEffect(() => {
    if (selectedDate && currentStep === STEPS.DATE_TIME) {
      fetchSlots(selectedDate);
    }
  }, [selectedDate, currentStep, fetchSlots]);

  // Handle booking submission
  const handleSubmit = async () => {
    // Honeypot check
    if (patientInfo.honeypot) {
      setError('Invalid submission');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/public/booking/clinic/${clinicSlug}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointment_type_id: selectedAppointmentType.id,
          practitioner_id: selectedPractitioner?.id || null,
          date: selectedDate,
          start_time: selectedTime.start_time,
          first_name: patientInfo.firstName,
          last_name: patientInfo.lastName,
          email: patientInfo.email,
          phone: patientInfo.phone,
          date_of_birth: patientInfo.dateOfBirth || null,
          reason: patientInfo.reason || null,
          honeypot_field: patientInfo.honeypot
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setConfirmationData(data.confirmation);
        setCurrentStep(STEPS.SUCCESS);
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Failed to complete booking');
      }
    } catch (err) {
      setError('Failed to connect to booking service');
    }
    setSubmitting(false);
  };

  // Generate date options (next X days)
  const generateDateOptions = () => {
    const dates = [];
    const minNoticeHours = clinicInfo?.settings?.min_notice_hours || 2;
    const maxAdvanceDays = clinicInfo?.settings?.max_advance_days || 60;
    
    const startDate = new Date();
    startDate.setHours(startDate.getHours() + minNoticeHours);
    
    for (let i = 0; i < maxAdvanceDays; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  // Get available practitioners for selected appointment type
  const getAvailablePractitioners = () => {
    if (!clinicInfo || !selectedAppointmentType) return [];
    return clinicInfo.practitioners.filter(p => 
      p.appointment_type_ids.includes(selectedAppointmentType.id)
    );
  };

  // Format date for display
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className={`${isEmbedMode ? 'min-h-[600px]' : 'min-h-screen'} bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center`}>
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          <p className="text-slate-600">Loading booking page...</p>
        </div>
      </div>
    );
  }

  if (error && !clinicInfo) {
    return (
      <div className={`${isEmbedMode ? 'min-h-[400px]' : 'min-h-screen'} bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4`}>
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-slate-800 mb-2">Booking Unavailable</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isEmbedMode ? 'min-h-[600px]' : 'min-h-screen'} bg-gradient-to-b from-slate-50 to-slate-100`}>
      {/* Header - Compact in embed mode */}
      <header className={`bg-white border-b border-slate-200 ${isEmbedMode ? 'py-3' : 'py-4'}`}>
        <div className={`max-w-3xl mx-auto px-4 flex items-center ${isEmbedMode ? 'gap-3' : 'gap-4'}`}>
          {clinicInfo?.clinic?.logo_url && (
            <img 
              src={clinicInfo.clinic.logo_url} 
              alt={clinicInfo.clinic.name}
              className={`${isEmbedMode ? 'w-10 h-10' : 'w-12 h-12'} rounded-lg object-cover`}
            />
          )}
          <div>
            <h1 className={`${isEmbedMode ? 'text-lg' : 'text-xl'} font-semibold text-slate-800`}>{clinicInfo?.clinic?.name}</h1>
            <p className="text-sm text-slate-500">{clinicInfo?.settings?.page_title || 'Book an Appointment'}</p>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      {currentStep < STEPS.SUCCESS && (
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-8">
            {[
              { step: STEPS.APPOINTMENT_TYPE, label: 'Service' },
              { step: STEPS.PRACTITIONER, label: 'Provider' },
              { step: STEPS.DATE_TIME, label: 'Date & Time' },
              { step: STEPS.PATIENT_INFO, label: 'Details' },
              { step: STEPS.CONFIRMATION, label: 'Confirm' }
            ].map(({ step, label }) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= step 
                    ? 'bg-teal-600 text-white' 
                    : 'bg-slate-200 text-slate-500'
                }`}>
                  {currentStep > step ? <Check className="w-4 h-4" /> : step}
                </div>
                <span className={`hidden sm:block ml-2 text-sm ${
                  currentStep >= step ? 'text-teal-600 font-medium' : 'text-slate-400'
                }`}>
                  {label}
                </span>
                {step < STEPS.CONFIRMATION && (
                  <ChevronRight className="w-4 h-4 text-slate-300 mx-2" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && currentStep !== STEPS.SUCCESS && (
        <div className="max-w-3xl mx-auto px-4 mb-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-700 text-sm">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 pb-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          
          {/* Step 1: Select Appointment Type */}
          {currentStep === STEPS.APPOINTMENT_TYPE && (
            <div className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Select a Service</h2>
              <div className="space-y-3">
                {clinicInfo?.appointment_types?.map(type => (
                  <button
                    key={type.id}
                    data-testid={`appointment-type-${type.id}`}
                    onClick={() => {
                      setSelectedAppointmentType(type);
                      setCurrentStep(STEPS.PRACTITIONER);
                    }}
                    className="w-full text-left p-4 rounded-lg border border-slate-200 hover:border-teal-500 hover:bg-teal-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-slate-800">{type.name}</h3>
                        <p className="text-sm text-slate-500 mt-1">
                          <Clock className="w-4 h-4 inline mr-1" />
                          {type.duration_minutes} minutes
                          {type.price > 0 && ` • £${type.price}`}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Select Practitioner */}
          {currentStep === STEPS.PRACTITIONER && (
            <div className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Select a Practitioner</h2>
              <div className="space-y-3">
                {clinicInfo?.settings?.allow_any_practitioner && (
                  <button
                    data-testid="practitioner-any"
                    onClick={() => {
                      setSelectedPractitioner(null);
                      setCurrentStep(STEPS.DATE_TIME);
                    }}
                    className="w-full text-left p-4 rounded-lg border border-slate-200 hover:border-teal-500 hover:bg-teal-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-800">Any Available Practitioner</h3>
                          <p className="text-sm text-slate-500">First available time slot</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </button>
                )}
                
                {getAvailablePractitioners().map(prac => (
                  <button
                    key={prac.id}
                    data-testid={`practitioner-${prac.id}`}
                    onClick={() => {
                      setSelectedPractitioner(prac);
                      setCurrentStep(STEPS.DATE_TIME);
                    }}
                    className="w-full text-left p-4 rounded-lg border border-slate-200 hover:border-teal-500 hover:bg-teal-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-800">{prac.name}</h3>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </button>
                ))}
              </div>
              
              <button 
                onClick={() => setCurrentStep(STEPS.APPOINTMENT_TYPE)}
                className="mt-4 text-teal-600 hover:text-teal-700 text-sm font-medium"
              >
                &larr; Back
              </button>
            </div>
          )}

          {/* Step 3: Select Date & Time */}
          {currentStep === STEPS.DATE_TIME && (
            <div className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Select Date & Time</h2>
              
              {/* Date Selection with Shadcn Calendar */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <CalendarIcon className="w-4 h-4 inline mr-1" />
                  Select Date
                </label>
                <div className="flex justify-center bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <Calendar
                    mode="single"
                    selected={selectedDate ? new Date(selectedDate + 'T00:00:00') : undefined}
                    onSelect={(date) => {
                      if (date) {
                        const dateStr = date.toISOString().split('T')[0];
                        setSelectedDate(dateStr);
                        setSelectedTime(null);
                      }
                    }}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const minDate = new Date(today);
                      minDate.setHours(minDate.getHours() + (clinicInfo?.settings?.min_notice_hours || 2));
                      const maxDate = new Date(today);
                      maxDate.setDate(maxDate.getDate() + (clinicInfo?.settings?.max_advance_days || 60));
                      return date < minDate || date > maxDate;
                    }}
                    className="rounded-lg"
                    data-testid="date-calendar"
                  />
                </div>
                {selectedDate && (
                  <p className="text-center text-sm text-teal-600 mt-2 font-medium">
                    Selected: {formatDate(selectedDate)}
                  </p>
                )}
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Available Times
                  </label>
                  
                  {loadingSlots ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      No available times on this date. Please select another date.
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {availableSlots.map((slot, idx) => (
                        <button
                          key={idx}
                          data-testid={`time-slot-${slot.start_time}`}
                          onClick={() => setSelectedTime(slot)}
                          className={`p-3 rounded-lg text-sm font-medium transition-colors ${
                            selectedTime?.start_time === slot.start_time
                              ? 'bg-teal-600 text-white'
                              : 'bg-slate-100 text-slate-700 hover:bg-teal-100'
                          }`}
                        >
                          {slot.start_time}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <div className="mt-6 flex justify-between">
                <button 
                  onClick={() => setCurrentStep(STEPS.PRACTITIONER)}
                  className="text-teal-600 hover:text-teal-700 text-sm font-medium"
                >
                  &larr; Back
                </button>
                
                <button
                  data-testid="continue-to-details"
                  onClick={() => setCurrentStep(STEPS.PATIENT_INFO)}
                  disabled={!selectedDate || !selectedTime}
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Patient Information */}
          {currentStep === STEPS.PATIENT_INFO && (
            <div className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Your Details</h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      data-testid="patient-first-name"
                      value={patientInfo.firstName}
                      onChange={(e) => setPatientInfo({...patientInfo, firstName: e.target.value})}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      data-testid="patient-last-name"
                      value={patientInfo.lastName}
                      onChange={(e) => setPatientInfo({...patientInfo, lastName: e.target.value})}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email *
                  </label>
                  <input
                    type="email"
                    data-testid="patient-email"
                    value={patientInfo.email}
                    onChange={(e) => setPatientInfo({...patientInfo, email: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Phone *
                  </label>
                  <input
                    type="tel"
                    data-testid="patient-phone"
                    value={patientInfo.phone}
                    onChange={(e) => setPatientInfo({...patientInfo, phone: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>
                
                {clinicInfo?.settings?.require_dob && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Date of Birth *</label>
                    <input
                      type="date"
                      data-testid="patient-dob"
                      value={patientInfo.dateOfBirth}
                      onChange={(e) => setPatientInfo({...patientInfo, dateOfBirth: e.target.value})}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reason for Appointment</label>
                  <textarea
                    data-testid="patient-reason"
                    value={patientInfo.reason}
                    onChange={(e) => setPatientInfo({...patientInfo, reason: e.target.value})}
                    rows={3}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder="Please briefly describe the reason for your visit..."
                  />
                </div>
                
                {/* Honeypot field - hidden from users, visible to bots */}
                <input
                  type="text"
                  name="website"
                  value={patientInfo.honeypot}
                  onChange={(e) => setPatientInfo({...patientInfo, honeypot: e.target.value})}
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              
              <div className="mt-6 flex justify-between">
                <button 
                  onClick={() => setCurrentStep(STEPS.DATE_TIME)}
                  className="text-teal-600 hover:text-teal-700 text-sm font-medium"
                >
                  &larr; Back
                </button>
                
                <button
                  data-testid="continue-to-confirm"
                  onClick={() => setCurrentStep(STEPS.CONFIRMATION)}
                  disabled={!patientInfo.firstName || !patientInfo.lastName || !patientInfo.email || !patientInfo.phone}
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                >
                  Review Booking
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Confirmation */}
          {currentStep === STEPS.CONFIRMATION && (
            <div className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Confirm Your Booking</h2>
              
              <div className="bg-slate-50 rounded-lg p-4 space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-slate-600">Service</span>
                  <span className="font-medium text-slate-800">{selectedAppointmentType?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Practitioner</span>
                  <span className="font-medium text-slate-800">
                    {selectedPractitioner?.name || 'Any Available'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Date</span>
                  <span className="font-medium text-slate-800">{formatDate(selectedDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Time</span>
                  <span className="font-medium text-slate-800">
                    {selectedTime?.start_time} - {selectedTime?.end_time}
                  </span>
                </div>
                <div className="border-t border-slate-200 pt-3">
                  <span className="text-slate-600">Patient</span>
                  <div className="font-medium text-slate-800">
                    {patientInfo.firstName} {patientInfo.lastName}
                  </div>
                  <div className="text-sm text-slate-500">{patientInfo.email}</div>
                  <div className="text-sm text-slate-500">{patientInfo.phone}</div>
                </div>
                {patientInfo.reason && (
                  <div className="border-t border-slate-200 pt-3">
                    <span className="text-slate-600">Reason</span>
                    <div className="text-sm text-slate-700 mt-1">{patientInfo.reason}</div>
                  </div>
                )}
              </div>
              
              {clinicInfo?.settings?.cancellation_policy && (
                <div className="text-sm text-slate-600 mb-6">
                  <strong>Cancellation Policy:</strong> {clinicInfo.settings.cancellation_policy}
                </div>
              )}
              
              <div className="flex justify-between">
                <button 
                  onClick={() => setCurrentStep(STEPS.PATIENT_INFO)}
                  className="text-teal-600 hover:text-teal-700 text-sm font-medium"
                >
                  &larr; Back
                </button>
                
                <button
                  data-testid="confirm-booking-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:bg-slate-400 transition-colors flex items-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Booking...
                    </>
                  ) : (
                    'Confirm Booking'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 6: Success */}
          {currentStep === STEPS.SUCCESS && (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              
              <h2 className="text-2xl font-semibold text-slate-800 mb-2">Booking Confirmed!</h2>
              <p className="text-slate-600 mb-6">{confirmationData?.message}</p>
              
              <div className="bg-slate-50 rounded-lg p-4 text-left max-w-md mx-auto mb-6">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Date</span>
                    <span className="font-medium">{formatDate(confirmationData?.date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Time</span>
                    <span className="font-medium">{confirmationData?.time} - {confirmationData?.end_time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Service</span>
                    <span className="font-medium">{confirmationData?.appointment_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">With</span>
                    <span className="font-medium">{confirmationData?.practitioner}</span>
                  </div>
                </div>
                
                {confirmationData?.clinic_address && (
                  <div className="border-t border-slate-200 mt-3 pt-3 text-sm">
                    <strong className="text-slate-800">{confirmationData?.clinic_name}</strong>
                    <div className="text-slate-600">{confirmationData?.clinic_address}</div>
                    {confirmationData?.clinic_phone && (
                      <div className="text-slate-600">Tel: {confirmationData?.clinic_phone}</div>
                    )}
                  </div>
                )}
              </div>
              
              <p className="text-sm text-slate-500">
                A confirmation has been recorded. You can contact the clinic if you need to make any changes.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Footer - Hidden in embed mode */}
      {!isEmbedMode && (
        <footer className="max-w-3xl mx-auto px-4 py-8 text-center text-sm text-slate-500">
          <p>Powered by LumeroFlow</p>
          {clinicInfo?.clinic?.phone && (
            <p className="mt-1">Questions? Call {clinicInfo.clinic.phone}</p>
          )}
        </footer>
      )}
      
      {/* Minimal embed footer */}
      {isEmbedMode && (
        <footer className="max-w-3xl mx-auto px-4 py-3 text-center">
          <p className="text-xs text-slate-400">Powered by LumeroFlow</p>
        </footer>
      )}
    </div>
  );
}
